/**
 * OAuth (Google/Facebook/Apple) dans le shell natif Capacitor.
 *
 * Google interdit l'OAuth dans une WebView embarquée classique : on route
 * donc le provider vers le navigateur système (@capacitor/browser), qui
 * redirige ensuite vers ce schéma custom — enregistré dans :
 *   - ios/App/App/Info.plist (CFBundleURLTypes)
 *   - android/app/src/main/AndroidManifest.xml (intent-filter du MainActivity)
 *   - Supabase Dashboard → Authentication → URL Configuration → Redirect URLs
 *     (vérifié présent : le `referrer` de `auth.flow_state` conserve bien le
 *     schéma custom au lieu de retomber sur la Site URL)
 *
 * Le retour est capté par le listener `appUrlOpen` (main.tsx), qui échange le
 * code PKCE contre une session via `supabase.auth.exchangeCodeForSession`.
 * Sur le web, ce module n'est jamais utilisé — cf. `handleOAuth` dans
 * otp-auth-form.tsx, gated par `Capacitor.isNativePlatform()`.
 */
export const NATIVE_OAUTH_REDIRECT_URL = 'fr.jeraisonne.app://auth-callback';

export type NativeOAuthCallback =
  | { kind: 'code'; code: string }
  | { kind: 'error'; message: string };

/**
 * Extrait le code PKCE (ou l'erreur) du deep link de retour OAuth.
 *
 * `supabase.auth.exchangeCodeForSession(authCode)` attend le CODE SEUL, pas
 * l'URL complète : lui passer l'URL envoie `auth_code: "fr.jeraisonne.app://…"`
 * au endpoint `/token?grant_type=pkce`, qui rejette. C'était le 2e bug du flow
 * natif (le 1er étant `flowType` resté à 'implicit', cf. lib/supabase.ts).
 *
 * Tolérant sur la forme du retour : Supabase renvoie `?code=…` en PKCE, mais
 * une annulation ou un refus de scope arrive en `?error=…` ; certaines
 * plateformes normalisent l'URL avec un `/` avant la query. On accepte aussi
 * un fragment `#` (implicit / erreurs Apple) pour ne jamais rester muet.
 *
 * ⚠️ La query et le fragment sont séparés AVANT le parsing. Google (et
 * Supabase) ajoutent un fragment de fin — souvent VIDE — au redirect PKCE :
 * `fr.jeraisonne.app://auth-callback?code=<uuid>#`. Un parsing naïf de toute la
 * chaîne (`code=<uuid>#`) via `URLSearchParams` ne retire PAS ce `#` (c'est un
 * concept d'URL, pas de query-string) → le code devient `<uuid>#`, que
 * `exchangeCodeForSession` envoie tel quel au endpoint `/token?grant_type=pkce`,
 * qui ne trouve alors aucun `flow_state` pour cet auth_code → erreur 404
 * `flow_state_not_found` (« invalid flow state ») et connexion Google/Facebook
 * cassée. On coupe donc au 1er `#`, puis on lit les paramètres des DEUX côtés.
 *
 * @returns `null` si l'URL n'est pas notre callback, ou si elle ne porte ni
 *   code ni erreur (rien à faire).
 */
export function parseNativeOAuthCallback(url: string): NativeOAuthCallback | null {
  if (!url.startsWith(NATIVE_OAUTH_REDIRECT_URL)) return null;

  // Reste = `[/]?[query][#fragment]`. On isole la query (avant le 1er `#`) du
  // fragment pour qu'un `#` de fin ne contamine jamais la dernière valeur.
  const rest = url.slice(NATIVE_OAUTH_REDIRECT_URL.length).replace(/^\/+/, '');
  const hashIndex = rest.indexOf('#');
  const queryPart = (hashIndex === -1 ? rest : rest.slice(0, hashIndex)).replace(/^\?/, '');
  const fragmentPart = hashIndex === -1 ? '' : rest.slice(hashIndex + 1);

  const params = new URLSearchParams(queryPart);
  // Fusionne les paramètres du fragment (erreurs Apple / implicit) sans écraser
  // ceux de la query, qui prime.
  for (const [key, value] of new URLSearchParams(fragmentPart)) {
    if (!params.has(key)) params.append(key, value);
  }

  // L'erreur prime : un retour peut porter `code` ET `error` (rare), et dans ce
  // cas le code est inexploitable.
  const error = params.get('error_description') ?? params.get('error');
  if (error) return { kind: 'error', message: error };

  const code = params.get('code');
  return code ? { kind: 'code', code } : null;
}
